import React, { useReducer, useEffect, useState } from 'react';
import HourlyRateInput from './HourlyRateInput';
import { Project, Member, ProjectCost } from '../types/api';
import { AllocationMember, MonthlyAllocation } from '../types/allocation';
import { allocationReducer, initialState, validateMonthlyAllocation, getActiveMembers } from '../utils/allocationReducer';
import { generateMonths } from '../utils/monthUtils';
import { ProjectMemberService, MemberService, MemberCostService } from '../services';
import type { MemberCosts } from '../services/memberCostService';
import AddMemberModal from './AddMemberModal';
import { getMemberRateForMonth } from '../utils/costUtils';

interface AllocationGridProps {
  selectedProject: Project | null;
  onAllocationChange: (members: AllocationMember[]) => void;
  onProjectCostsChange?: (costs: ProjectCost[]) => void; // Callback for project costs
  initialMembers?: AllocationMember[]; // For edit mode
}

/**
 * Role shown beside the hourly rate in the first column.
 *
 * Members picked from the bench arrive with role_name 'Bench', which marks
 * where they came from rather than naming a project role, so it is not shown.
 */
const roleLabel = (member: Member): string => {
  const label = member.role?.name || member.role_name || member.roles || 'N/A';
  return label === 'Bench' ? '' : label;
};

const AllocationGrid: React.FC<AllocationGridProps> = ({
  selectedProject,
  onAllocationChange,
  onProjectCostsChange,
  initialMembers,
}) => {
  const [state, dispatch] = useReducer(allocationReducer, initialState);
  const [lastLoadedProjectId, setLastLoadedProjectId] = useState<number | null>(null);
  const [memberCosts, setMemberCosts] = useState<MemberCosts>({});
  const [isInitialized, setIsInitialized] = useState(false);
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);

  // Initialize with saved members in edit mode (ONLY ONCE)
  useEffect(() => {
    if (initialMembers && initialMembers.length > 0 && selectedProject && !isInitialized) {
      console.log('📝 Edit Mode: Initializing with saved members', initialMembers);
      
      const currentDate = new Date();
      const startDate = currentDate.toISOString().split('T')[0];
      const months = generateMonths(startDate, 12);
      
      dispatch({ type: 'SET_MONTHS', payload: months });
      dispatch({ type: 'SET_MEMBERS', payload: initialMembers });
      dispatch({ type: 'SET_LOADING', payload: false });
      setLastLoadedProjectId(selectedProject.id);
      setIsInitialized(true); // Mark as initialized
      
      return; // Skip loading from API
    }
  }, [initialMembers, selectedProject, isInitialized]); // Add isInitialized to deps

  // Reset initialization flag when project changes or when not in edit mode
  useEffect(() => {
    if (!initialMembers || !selectedProject) {
      setIsInitialized(false);
    }
  }, [selectedProject?.id, initialMembers]);

  // Load project members when project changes (for new dashboards - NOT edit mode)
useEffect(() => {
  // Only load if:
  // 1. A project is selected
  // 2. Not in edit mode (no initialMembers)
  // 3. Project has changed (different ID than last loaded)
  // 4. Not already initialized (to prevent conflicts)
  if (
    selectedProject && 
    !initialMembers && 
    selectedProject.id !== lastLoadedProjectId &&
    !isInitialized
  ) {
    console.log('🆕 Loading project members from API for project:', selectedProject.id);
    // Start allocation from current month to end of project
    const currentDate = new Date();
    const startDate = currentDate.toISOString().split('T')[0];
    const months = generateMonths(startDate, 12);
    dispatch({ type: 'SET_MONTHS', payload: months });
    loadProjectMembers();
    setLastLoadedProjectId(selectedProject.id);
    setIsInitialized(true); // Mark as initialized to prevent re-loading
  }
}, [selectedProject, initialMembers, lastLoadedProjectId, isInitialized]); // eslint-disable-line react-hooks/exhaustive-deps


  // Load project members from API
  const loadProjectMembers = async () => {
    if (!selectedProject) return;

    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    console.log('🔄 Loading project members for project ID:', selectedProject.id);
    
    try {
      // Fetch project members with allocations
      const response = await ProjectMemberService.getProjectMembers(selectedProject.id, undefined, true);
      const projectMembers = response.members;
      console.log('✅ Project members loaded:', projectMembers.length, 'members');
      console.log('✅ Has allocations:', response.hasAllocations);
      console.log('✅ Budget data:', response.budget);
      console.log('✅ Project costs:', response.projectCosts);

      
      /*
      // Determine months - use earliest allocation month if available, otherwise current month
      let startDate = new Date().toISOString().split('T')[0];
      if (response.hasAllocations && projectMembers.length > 0) {
        // Find earliest month from allocations
        const allMonths: string[] = [];
        projectMembers.forEach(pm => {
          if (pm.allocations) {
            Object.keys(pm.allocations).forEach(month => allMonths.push(month));
          }
        });
        if (allMonths.length > 0) {
          const sortedMonths = allMonths.sort();
          // Find the earliest month from allocations
          const earliestMonth = sortedMonths[0];
          // Use current month if earliest allocation is before current month
          const now = new Date();
          const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
          const [allocYear, allocMonth] = earliestMonth.split('-').map(Number);
          const allocMonthStr = `${allocYear}-${String(allocMonth).padStart(2, '0')}`;
          // If the allocation's earliest month is before current month, start from current month
          if (allocMonthStr < currentMonthStr) {
            startDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
          } else {
            startDate = `${allocYear}-${String(allocMonth).padStart(2, '0')}-01`;
          }
        }
      }
        */

      // Determine months - use earliest allocation month if available, otherwise current month
      let startDate = new Date().toISOString().split('T')[0];
      if (response.hasAllocations && projectMembers.length > 0) {
        // Find earliest month from allocations
        const allMonths: string[] = [];
        projectMembers.forEach(pm => {
          if (pm.allocations) {
            Object.keys(pm.allocations).forEach(month => allMonths.push(month));
          }
        });
        if (allMonths.length > 0) {
          const sortedMonths = allMonths.sort();
          // Get the earliest month and ensure it's normalized to first day of month
          // Parse the date string directly to avoid timezone issues
          const earliestMonth = sortedMonths[0];
          // Extract year and month from YYYY-MM-DD format
          const [year, month] = earliestMonth.split('-').map(Number);
          // Format directly as YYYY-MM-01 to avoid timezone conversion issues
          const monthStr = String(month).padStart(2, '0');
          startDate = `${year}-${monthStr}-01`;
        }
      }
      
      const months = generateMonths(startDate, 12);
      dispatch({ type: 'SET_MONTHS', payload: months });

      // Extract month keys for cost fetching (YYYY-MM format)
      const monthKeys = months.map(m => m.monthStart.substring(0, 7));
      console.log('monthKeys', monthKeys);
      
      // Extract member IDs
      const memberIds = projectMembers.map(pm => pm.id);

      // Fetch month-specific costs for all members
      console.log('💰 Fetching month-specific costs for', memberIds.length, 'members across', monthKeys.length, 'months');
      const costs = await MemberCostService.getMemberCostsByMonths(
        memberIds,
        monthKeys
      );
      
      console.log('✅ Month-specific costs loaded:', costs);
      setMemberCosts(costs);

      // Create allocation members from project members
      // Auto-populate allocations if they exist
      const allocationMembers: AllocationMember[] = projectMembers.map((pm) => {
        // Convert allocations from API format to monthlyAllocations format
        const monthlyAllocations: { [monthStart: string]: number } = {};
        const allocations = pm.allocations;
        let savedClientRate: number | undefined;
        if (allocations) {
          Object.keys(allocations).forEach(monthStart => {
            const alloc = allocations[monthStart];
            monthlyAllocations[monthStart] = alloc?.allocated_hours || 0;
            // The client rate is per member, so any saved month carries the same value
            if (savedClientRate === undefined && alloc?.client_hourly_rate != null) {
              savedClientRate = alloc.client_hourly_rate;
            }
          });
        }

        // Default the client rate to the member's own hourly cost (month-specific
        // average when available, otherwise the rate returned with the member)
        const monthCosts = Object.values(costs[pm.id.toString()] || {});
        const averageCost = monthCosts.length > 0
          ? monthCosts.reduce((acc, cost) => acc + cost, 0) / monthCosts.length
          : (pm.per_hour_cost || 0);

        // The saved roster rate wins over a rate read off an allocation row
        if (pm.client_hourly_rate != null) {
          savedClientRate = pm.client_hourly_rate;
        }

        return {
        id: pm.id,
        member: {
          id: pm.id,
          firstname: pm.firstname,
          lastname: pm.lastname,
          login: pm.login,
          role_name: pm.role_name || 'Team Member',
          project_name: pm.project_name,
          customer_name: pm.customer_name,
            per_hour_cost: pm.per_hour_cost || 0,
        },
          selected: false, // No selection needed for project budget
        removed: false,
          monthlyAllocations: monthlyAllocations, // Auto-populate if exists
          clientHourlyRate: savedClientRate !== undefined ? savedClientRate : averageCost,
        };
      });

      console.log('✅ Allocation members created:', allocationMembers.length, 'members');
      if (response.hasAllocations) {
        console.log('✅ Allocations auto-populated from saved data');
      }
      
      dispatch({ type: 'SET_MEMBERS', payload: allocationMembers });
      dispatch({ type: 'SET_LOADING', payload: false });
      
      // Convert and pass project costs to parent
      if (onProjectCostsChange) {
        if (response.projectCosts && response.projectCosts.length > 0) {
          // Convert API format to ProjectCost format
          const convertedCosts: ProjectCost[] = response.projectCosts.map((cost: any) => ({
            id: cost.category || 'other',
            description: cost.description || cost.category || 'Other',
            billable: cost.billable !== undefined ? cost.billable : true,
            monthlyCosts: cost.monthlyCosts || {}
          }));
          console.log('✅ Project costs auto-populated from saved data:', convertedCosts);
          onProjectCostsChange(convertedCosts);
        } else {
          // Clear project costs if no saved costs exist
          console.log('ℹ️ No saved project costs found, clearing costs');
          onProjectCostsChange([]);
        }
      }
      
      // Always notify parent about allocation changes
      onAllocationChange(allocationMembers);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load project members';
      console.error('❌ Error loading project members:', error);
      
      // Provide helpful error message
      let userMessage = errorMessage;
      if (errorMessage.includes('404')) {
        userMessage = 'No team members found for this project. Only Engineering and QA roles are included.';
      } else if (errorMessage.includes('Network Error')) {
        userMessage = 'Cannot connect to backend API. Please ensure the backend server is running on port 5000.';
      }
      
      dispatch({ type: 'SET_ERROR', payload: userMessage });
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Handle monthly allocation update
  const handleMonthlyAllocationChange = (
    memberId: number,
    monthStart: string,
    hours: number
  ) => {
    if (validateMonthlyAllocation(memberId, monthStart, hours, state.members)) {
      dispatch({
        type: 'UPDATE_MONTHLY_ALLOCATION',
        payload: { memberId, monthStart, hours },
      });
    }
  };


  // Copy the previous month's hours into the given month for every member in the grid
  const handleCopyFromPreviousMonth = (monthIndex: number) => {
    if (monthIndex <= 0) return;

    const fromMonth = state.months[monthIndex - 1];
    const toMonth = state.months[monthIndex];
    if (!fromMonth || !toMonth) return;

    dispatch({
      type: 'COPY_MONTH_ALLOCATIONS',
      payload: { fromMonthStart: fromMonth.monthStart, toMonthStart: toMonth.monthStart },
    });
  };

  // Check if a member has varying costs across months
  const hasCostChanges = (memberId: number): boolean => {
    const memberIdStr = memberId.toString();
    if (!memberCosts[memberIdStr]) return false;
    
    const costs = Object.values(memberCosts[memberIdStr]);
    if (costs.length <= 1) return false;
    
    const firstCost = costs[0];
    return costs.some(cost => cost !== firstCost);
  };

  // Get average cost for a member
  const getAverageCost = (memberId: number): number => {
    const memberIdStr = memberId.toString();
    if (!memberCosts[memberIdStr]) {
      const member = state.members.find(m => m.id === memberId);
      return member?.member.per_hour_cost || 0;
    }
    
    const costs = Object.values(memberCosts[memberIdStr]);
    if (costs.length === 0) return 0;
    
    const sum = costs.reduce((acc, cost) => acc + cost, 0);
    return sum / costs.length;
  };

  // Handle member selection
  const handleMemberSelection = (memberId: number) => {
    dispatch({ type: 'TOGGLE_MEMBER_SELECTION', payload: memberId });
  };

  // Handle select all
  const handleSelectAll = () => {
    dispatch({ type: 'SELECT_ALL_MEMBERS' });
  };

  // Handle deselect all
  const handleDeselectAll = () => {
    dispatch({ type: 'DESELECT_ALL_MEMBERS' });
  };

  // Handle remove selected members
  const handleRemoveSelected = () => {
    dispatch({ type: 'REMOVE_SELECTED_MEMBERS' });
  };

  // Handle removing a single member
  const handleRemoveMember = (memberId: number) => {
    dispatch({ type: 'REMOVE_MEMBER', payload: memberId });
  };

  // Handle adding new members from the picker
  const handleAddMembers = async (newMembers: AllocationMember[]) => {
    if (!selectedProject || newMembers.length === 0) return;

    // Reducer adds one member at a time
    newMembers.forEach(member => {
      dispatch({
        type: 'ADD_MEMBER',
        payload: {
          ...member,
          // Client rate starts at the member's own rate, as it does for the rest of the grid
          clientHourlyRate: member.member.per_hour_cost || 0,
        },
      });
    });

    if (state.months.length === 0) {
      console.warn('⚠️ Cannot fetch costs for new members: months not yet initialized');
      return;
    }

    // Pull month-specific costs so the new rows cost out like the existing ones
    try {
      const newMemberIds = newMembers.map(m => m.id);
      const monthKeys = state.months.map(m => m.monthStart.substring(0, 7));
      const newCosts = await MemberCostService.getMemberCostsByMonths(
        newMemberIds,
        monthKeys
      );

      setMemberCosts(prevCosts => ({ ...prevCosts, ...newCosts }));

      newMembers.forEach(member => {
        const costsForMember = newCosts[member.id.toString()];
        if (!costsForMember || Object.keys(costsForMember).length === 0) return;

        const values = Object.values(costsForMember);
        const averageCost = values.reduce((sum, cost) => sum + cost, 0) / values.length;
        if (averageCost > 0) {
          dispatch({ type: 'UPDATE_HOURLY_COST', payload: { memberId: member.id, cost: averageCost } });
          dispatch({ type: 'UPDATE_CLIENT_HOURLY_RATE', payload: { memberId: member.id, rate: averageCost } });
        }
      });
    } catch (error) {
      console.error('❌ Error fetching costs for new members:', error);
      // Fall back to the member's own per_hour_cost
    }
  };

  // Rate charged to the client - defaults to the member's own hourly rate
  const getClientHourlyRate = (allocationMember: AllocationMember): number => {
    if (allocationMember.clientHourlyRate !== undefined && allocationMember.clientHourlyRate !== null) {
      return allocationMember.clientHourlyRate;
    }
    return getAverageCost(allocationMember.id);
  };

  // Handle client hourly rate update
  const handleClientHourlyRateChange = (memberId: number, value: string) => {
    const rate = value === '' ? 0 : parseFloat(value);
    if (isNaN(rate) || rate < 0) {
      return;
    }
    dispatch({ type: 'UPDATE_CLIENT_HOURLY_RATE', payload: { memberId, rate } });
  };

  // Notify parent of allocation changes
  useEffect(() => {
    // Skip notification if we just initialized from initialMembers
    if (isInitialized && initialMembers && initialMembers.length > 0) {
      // Only notify if members actually changed (not just initial load)
      const currentActive = getActiveMembers(state.members);
      const initialActive = getActiveMembers(initialMembers);
      
      // Compare member IDs and allocations to detect real changes
      const hasRealChanges = 
        currentActive.length !== initialActive.length ||
        currentActive.some((member, idx) => {
          const initialMember = initialActive[idx];
          if (!initialMember || member.id !== initialMember.id) return true;
          
          // Check if the client rate changed
          if (member.clientHourlyRate !== initialMember.clientHourlyRate) return true;

          // Check if allocations changed
          const currentAllocations = JSON.stringify(member.monthlyAllocations);
          const initialAllocations = JSON.stringify(initialMember.monthlyAllocations);
          return currentAllocations !== initialAllocations;
        });
      
      if (hasRealChanges) {
        onAllocationChange(currentActive);
      }
    } else {
      // Normal mode - always notify
      onAllocationChange(getActiveMembers(state.members));
    }
  }, [state.members]); // Remove onAllocationChange from deps to prevent loop


  if (!selectedProject) {
    return null;
  }

  if (state.loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading project members...</p>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-8">
          <div className="text-red-600 mb-2">
            <svg className="h-8 w-8 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-sm text-red-600">{state.error}</p>
          <button
            onClick={loadProjectMembers}
            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const activeMembers = getActiveMembers(state.members);
  const selectedCount = activeMembers.filter(m => m.selected).length;
  const existingMemberIds = activeMembers.map(m => m.id);

  return (
      <div className="bg-white rounded-lg shadow">
      {/* Header with Actions */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center gap-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Team Allocation Grid</h3>
              <p className="text-sm text-gray-500">
                Members are fetched from Redmine. Add or remove members as needed - the team,
                allocations and client rates are saved to the project budget.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setIsAddMemberModalOpen(true)}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 flex items-center gap-2"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Member
              </button>
              {selectedCount > 0 && (
                <button
                  onClick={handleRemoveSelected}
                  className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 flex items-center gap-2"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Remove Selected ({selectedCount})
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Selection Controls */}
        {activeMembers.length > 0 && (
          <div className="px-6 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {selectedCount > 0 ? (
                <span>{selectedCount} member(s) selected</span>
              ) : (
                <span>Select members to remove</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handleSelectAll} className="text-sm text-blue-600 hover:text-blue-800">
                Select All
              </button>
              <span className="text-gray-300">|</span>
              <button onClick={handleDeselectAll} className="text-sm text-gray-600 hover:text-gray-800">
                Deselect All
              </button>
            </div>
          </div>
        )}

        {/* Grid */}
        <div className="overflow-x-auto overflow-y-auto max-h-[75vh] min-h-[320px]">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                {/* Frozen member info column (name, email, role, internal rate) */}
                <th className="sticky top-0 left-0 z-30 bg-gray-50 w-64 min-w-[16rem] px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={activeMembers.length > 0 && activeMembers.every(m => m.selected)}
                      onChange={(e) => (e.target.checked ? handleSelectAll() : handleDeselectAll())}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      aria-label="Select all team members"
                    />
                    <span>Team Member</span>
                  </div>
                </th>
                {/* Frozen client rate column */}
                <th className="sticky top-0 left-64 z-30 bg-gray-50 w-40 min-w-[10rem] px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                  Hourly Rate for Client (₹)
                </th>
                
                {/* Monthly columns */}
                {state.months.map((month, monthIndex) => (
                  <th key={month.monthStart} className="w-44 min-w-[11rem] px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                    <div className="text-center">
                      <div className="font-medium">{month.label}</div>
                      <div className="text-[10px] sm:text-xs text-gray-400">Max 160hrs</div>
                      {monthIndex > 0 && activeMembers.length > 0 && (
                        <button
                          type="button"
                          onClick={() => handleCopyFromPreviousMonth(monthIndex)}
                          className="mt-1.5 w-full inline-flex items-center justify-center gap-1 px-2 py-1 text-[11px] font-medium normal-case tracking-normal rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800 active:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-colors"
                          title={`Copy hours from ${state.months[monthIndex - 1].label} into ${month.label} for all members`}
                        >
                          <svg className="h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                          </svg>
                          <span className="whitespace-nowrap">Copy from last month</span>
                        </button>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            
            <tbody className="bg-white divide-y divide-gray-200">
              {activeMembers.map((allocationMember) => (
                <tr
                  key={allocationMember.id}
                  className={`group hover:bg-gray-50 ${allocationMember.selected ? 'bg-blue-50' : ''}`}
                >
                  {/* Frozen: selection, name, email, role and internal hourly rate */}
                  <td className={`sticky left-0 z-10 ${allocationMember.selected ? 'bg-blue-50' : 'bg-white'} group-hover:bg-gray-50 w-64 min-w-[16rem] px-2 sm:px-3 py-2 sm:py-4 border-r border-gray-200`}>
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={allocationMember.selected || false}
                        onChange={() => handleMemberSelection(allocationMember.id)}
                        className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        aria-label={`Select ${allocationMember.member.firstname} ${allocationMember.member.lastname}`}
                      />
                      <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 truncate" title={`${allocationMember.member.firstname} ${allocationMember.member.lastname}`}>
                      {allocationMember.member.firstname} {allocationMember.member.lastname}
                    </div>
                    <div className="text-xs text-gray-500 truncate" title={allocationMember.member.login}>
                      {allocationMember.member.login}
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-xs">
                      {roleLabel(allocationMember.member) && (
                        <>
                          <span
                            className="truncate max-w-[9rem] text-gray-600 cursor-help"
                            title={roleLabel(allocationMember.member)}
                          >
                            {roleLabel(allocationMember.member)}
                          </span>
                          <span className="text-gray-300">|</span>
                        </>
                      )}
                      <span className="font-medium text-gray-900 whitespace-nowrap">
                        ₹{getAverageCost(allocationMember.id).toFixed(2)}/hr
                      </span>
                      {hasCostChanges(allocationMember.id) && (
                        <span className="text-yellow-600 flex items-center whitespace-nowrap" title="Cost varies by month">
                          <svg className="w-3 h-3 mr-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          Varies
                        </span>
                      )}
                    </div>
                      </div>
                      <button
                        onClick={() => handleRemoveMember(allocationMember.id)}
                        className="shrink-0 text-gray-300 hover:text-red-600 p-1"
                        title="Remove from team"
                        aria-label={`Remove ${allocationMember.member.firstname} ${allocationMember.member.lastname} from team`}
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>

                  {/* Frozen: rate billed to the client (defaults to the member's own rate) */}
                  <td className={`sticky left-64 z-10 ${allocationMember.selected ? 'bg-blue-50' : 'bg-white'} group-hover:bg-gray-50 w-40 min-w-[10rem] px-2 sm:px-3 py-2 sm:py-4 whitespace-nowrap border-r border-gray-300`}>
                    <div className="flex items-center">
                      <span className="mr-1 text-sm text-gray-500">₹</span>
                      <HourlyRateInput
                        value={getClientHourlyRate(allocationMember)}
                        onChange={(v) =>
                          handleClientHourlyRateChange(allocationMember.id, v)
                        }
                        className="w-24 px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0"
                        title="Rate charged to the client for this member"
                      />
                    </div>
                  </td>

                  {state.months.map((month) => {
                    // Hours are costed at the TMIS employee rate for that month
                    const costForMonth = getMemberRateForMonth(allocationMember, memberCosts, month.monthStart);
                    const hours = allocationMember.monthlyAllocations[month.monthStart] || 0;
                    const monthlyCost = hours * costForMonth;
                    // Revenue is what we bill for those hours: the client rate
                    const clientRate = getClientHourlyRate(allocationMember);
                    const monthlyRevenue = hours * clientRate;
                    
                    return (
                      <td key={month.monthStart} className="w-44 min-w-[11rem] px-2 sm:px-3 py-2 sm:py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <input
                            type="number"
                            min="0"
                            max="160"
                            step="1"
                            inputMode="numeric"
 onKeyDown={(e) => { if (["." , ",", "e", "E", "+", "-"].includes(e.key)) e.preventDefault(); }}
 value={allocationMember.monthlyAllocations[month.monthStart] || ''}
                            onChange={(e) => {
                              const hours = parseInt(e.target.value, 10) || 0;
                              handleMonthlyAllocationChange(allocationMember.id, month.monthStart, hours);
                            }}
                            className="w-16 sm:w-20 px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="0"
                          />
                          {hours > 0 && (
                            <div className="mt-1 text-[10px] leading-tight">
                              <div className="text-gray-500" title={`Cost: ₹${costForMonth}/hr × ${hours}hrs = ₹${monthlyCost.toFixed(2)}`}>
                                <span className="text-green-600">₹{monthlyCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                                <span className="text-gray-400 ml-1">(₹{costForMonth}/hr)</span>
                              </div>
                              <div className="text-gray-500" title={`Revenue: ₹${clientRate}/hr × ${hours}hrs = ₹${monthlyRevenue.toFixed(2)}`}>
                                <span className="text-blue-600">₹{monthlyRevenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                                <span className="text-gray-400 ml-1">rev</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>

            {/* Frozen totals row - hours, cost and revenue per month across the whole team */}
            {activeMembers.length > 0 && (
              <tfoot>
                <tr>
                  <td className="sticky bottom-0 left-0 z-30 bg-gray-100 w-64 min-w-[16rem] px-2 sm:px-3 py-2 sm:py-3 border-t border-r border-gray-300">
                    <div className="text-sm font-semibold text-gray-900">Monthly Total</div>
                    <div className="text-xs text-gray-500">{activeMembers.length} member(s)</div>
                  </td>
                  <td className="sticky bottom-0 left-64 z-30 bg-gray-100 w-40 min-w-[10rem] px-2 sm:px-3 py-2 sm:py-3 border-t border-r border-gray-300"></td>
                  {state.months.map((month) => {
                    const monthHours = activeMembers.reduce(
                      (sum, m) => sum + (m.monthlyAllocations[month.monthStart] || 0),
                      0
                    );
                    const monthCost = activeMembers.reduce(
                      (sum, m) =>
                        sum +
                        (m.monthlyAllocations[month.monthStart] || 0) *
                          getMemberRateForMonth(m, memberCosts, month.monthStart),
                      0
                    );
                    // Revenue billed for the month: hours x each member's client rate
                    const monthRevenue = activeMembers.reduce(
                      (sum, m) =>
                        sum + (m.monthlyAllocations[month.monthStart] || 0) * getClientHourlyRate(m),
                      0
                    );

                    return (
                      <td
                        key={month.monthStart}
                        className="sticky bottom-0 z-20 bg-gray-100 w-44 min-w-[11rem] px-2 sm:px-3 py-2 sm:py-3 whitespace-nowrap border-t border-gray-300"
                      >
                        <div className="text-sm font-semibold text-gray-900">
                          {monthHours.toLocaleString('en-IN', { maximumFractionDigits: 2 })} hrs
                        </div>
                        <div className="text-[10px] text-green-700" title="Employee cost for the month">
                          ₹{monthCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })} cost
                        </div>
                        <div className="text-[10px] text-blue-700" title="Revenue billed for the month">
                          ₹{monthRevenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })} rev
                        </div>
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Empty state */}
        {activeMembers.length === 0 && (
          <div className="text-center py-8">
            <div className="mx-auto h-12 w-12 text-gray-400">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No team members</h3>
            <p className="mt-1 text-sm text-gray-500 mb-4">
              No team members found for this project. Members are fetched from Redmine -
              use "Add Member" to build the team by hand.
            </p>
            <button
              onClick={() => setIsAddMemberModalOpen(true)}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
            >
              Add Member
            </button>
          </div>
        )}

        {/* Add Member Modal */}
        <AddMemberModal
          isOpen={isAddMemberModalOpen}
          onClose={() => setIsAddMemberModalOpen(false)}
          onAddMembers={handleAddMembers}
          existingMemberIds={existingMemberIds}
        />
      </div>
  );
};

export default AllocationGrid;
